/**
 * Formulario de configuración de Cierre (Closure/Mufa)
 * Permite configurar bandejas, empalmes y splitters de primer nivel
 * @module components/forms/CierreConfigForm
 */

import { useState } from 'react'
import {
  Plus,
  Trash2,
  Box,
  GitBranch,
  Layers,
  AlertTriangle,
  Link2
} from 'lucide-react'
import { useFiberStore } from '@/stores'
import { mutarExtremoFibra, liberarFibraPorExtremo } from '@/services/connectionService'
import { cableEndpointAtNode } from '@/utils/cableEndpoints'
import { cableLabelForSelect } from '@/utils/cableDisplayLabel'
import { listFibrasParaPicker } from '@/utils/cableFibrasPicker'

type TipoCaja = 'Domo' | 'Tipo bote' | 'Horizontal' | 'Vertical' | 'Inline'
type TipoEmpalme = 'fusión' | 'mecánico'
type TipoSplitter = 'balanceado' | 'desbalanceado'
type RatioSplitter = '1:2' | '1:4' | '1:8' | '1:16' | '1:32' | '1:64'

interface CableDisponible {
  id: string
  codigo?: string
  nombre?: string
  tipo?: string
  numeroFibras?: number
  fibras?: number
  tieneBuffers?: boolean
  buffers?: Array<{
    numero: number
    color: string
    fibras: Array<{ numero: number; color: string; estado: string; fibraId: string }>
  }>
}

interface FibraEmpalme {
  cableId: string
  buffer?: number
  fibra: number
  color: string
  /** ID real de la fibra en el cable (vacío hasta que se elige una fibra de verdad). */
  fibraId?: string
}

interface EmpalmeBandeja {
  id: string
  posicion: number
  fibraEntrada: FibraEmpalme
  fibraSalida: FibraEmpalme
  tipo: TipoEmpalme
  atenuacion?: number
  notas?: string
}

interface Bandeja {
  nivel: number
  capacidad: number
  empalmes: EmpalmeBandeja[]
}

interface SplitterCierre {
  id: string
  tipo: TipoSplitter
  ratio: RatioSplitter
  bandeja: number
  posicion: number
  fibraEntrada?: {
    cableId: string
    buffer?: number
    fibra: number
    color: string
  }
  salidasUsadas: number
  atenuacion: number
}

interface CableConectado {
  cableId: string
  direccion: 'entrada' | 'salida' | 'paso'
  fibrasUsadas: number
  puerto?: number
}

interface CierreConfig {
  tipoCaja: TipoCaja
  modelo: string
  fabricante?: string
  capacidadFibras: number
  numeroBandejas: number
  bandejas: Bandeja[]
  splitters: SplitterCierre[]
  cablesConectados: CableConectado[]
  estadoGeneral: 'operativo' | 'mantenimiento' | 'fuera_servicio'
}

interface CierreConfigFormProps {
  config?: CierreConfig
  onChange: (config: CierreConfig) => void
  cablesDisponibles?: CableDisponible[]
  /** ID de este Cierre (si ya existe). Sin esto no se puede saber qué extremo
   *  de cada cable marcar, así que los empalmes no tocan Cable.fibras. */
  nodeId?: string
}

const TIPOS_CAJA: TipoCaja[] = ['Domo', 'Tipo bote', 'Horizontal', 'Vertical', 'Inline']
const RATIOS_SPLITTER: RatioSplitter[] = ['1:2', '1:4', '1:8', '1:16', '1:32', '1:64']
const CAPACIDADES_CIERRE = [24, 48, 72, 96, 144, 288]

const ATENUACION_SPLITTER: Record<RatioSplitter, number> = {
  '1:2': 3.5,
  '1:4': 7.0,
  '1:8': 10.5,
  '1:16': 14.0,
  '1:32': 17.5,
  '1:64': 21.0,
}

const ATENUACION_EMPALME: Record<TipoEmpalme, number> = {
  'fusión': 0.1,
  'mecánico': 0.5,
}

const COLORES_FIBRA = [
  'azul', 'naranja', 'verde', 'marrón', 'gris', 'blanco',
  'rojo', 'negro', 'amarillo', 'violeta', 'rosa', 'turquesa'
]

export function CierreConfigForm({ config, onChange, cablesDisponibles = [], nodeId }: CierreConfigFormProps) {
  const cables = useFiberStore((state) => state.cables)
  const updateCable = useFiberStore((state) => state.updateCable)

  /** Mismo patrón que NAPConfigForm: marca/libera, en Cable.fibras, el extremo
   *  de una fibra que toca ESTE Cierre. No bloquea la UI del formulario. */
  const marcarFibraEnEsteNodo = (
    cableId: string | undefined,
    bufferNum: number | undefined,
    fibraNum: number | undefined,
    cambio: { estado: 'fusionado' } | { estado: 'libre' }
  ) => {
    if (!nodeId || !cableId || !fibraNum) return
    const cable = cables.find((c) => c.id === cableId)
    if (!cable) return
    const extremo = cableEndpointAtNode(cable as any, nodeId)
    if (!extremo) return

    const actualizado =
      cambio.estado === 'libre'
        ? liberarFibraPorExtremo(cable as any, extremo, bufferNum ?? null, fibraNum)
        : mutarExtremoFibra(cable as any, extremo, bufferNum ?? null, fibraNum, { estado: 'fusionado', conectadoA: null })

    void updateCable(actualizado as any)
  }

  const getInitialConfig = (): CierreConfig => {
    const numBandejas = config?.numeroBandejas || 4
    const defaultBandejas = Array.from({ length: numBandejas }, (_, i) => ({
      nivel: i + 1,
      capacidad: 12,
      empalmes: []
    }))
    
    return {
      tipoCaja: config?.tipoCaja || 'Domo',
      modelo: config?.modelo || '',
      fabricante: config?.fabricante,
      capacidadFibras: config?.capacidadFibras || 48,
      numeroBandejas: numBandejas,
      bandejas: config?.bandejas || defaultBandejas,
      splitters: config?.splitters || [],
      cablesConectados: config?.cablesConectados || [],
      estadoGeneral: config?.estadoGeneral || 'operativo',
    }
  }
  
  const [localConfig, setLocalConfig] = useState<CierreConfig>(getInitialConfig())
  const [expandedBandeja, setExpandedBandeja] = useState<number | null>(1)

  const updateConfig = (updates: Partial<CierreConfig>) => {
    const newConfig = { ...localConfig, ...updates }
    setLocalConfig(newConfig)
    onChange(newConfig)
  }

  const updateNumeroBandejas = (num: number) => {
    const bandejas = Array.from({ length: num }, (_, i) => {
      const existing = localConfig.bandejas.find(b => b.nivel === i + 1)
      return existing || { nivel: i + 1, capacidad: 12, empalmes: [] }
    })
    updateConfig({ numeroBandejas: num, bandejas })
  }

  const addEmpalme = (bandejaNum: number) => {
    const bandeja = localConfig.bandejas.find(b => b.nivel === bandejaNum)
    if (!bandeja) return

    // Sin cable/fibra preelegidos -- si no, se marcaría como usada una fibra
    // que el usuario nunca eligió a propósito.
    const nuevoEmpalme: EmpalmeBandeja = {
      id: `EMP-${Date.now()}`,
      posicion: bandeja.empalmes.length + 1,
      fibraEntrada: { cableId: '', fibra: 0, color: COLORES_FIBRA[0] },
      fibraSalida: { cableId: '', fibra: 0, color: COLORES_FIBRA[0] },
      tipo: 'fusión',
      atenuacion: ATENUACION_EMPALME['fusión']
    }

    const newBandejas = localConfig.bandejas.map(b => {
      if (b.nivel === bandejaNum) {
        return { ...b, empalmes: [...b.empalmes, nuevoEmpalme] }
      }
      return b
    })
    updateConfig({ bandejas: newBandejas })
  }

  const removeEmpalme = (bandejaNum: number, empalmeId: string) => {
    const bandeja = localConfig.bandejas.find(b => b.nivel === bandejaNum)
    const empalme = bandeja?.empalmes.find(e => e.id === empalmeId)
    if (empalme) {
      if (empalme.fibraEntrada.fibraId) {
        marcarFibraEnEsteNodo(empalme.fibraEntrada.cableId, empalme.fibraEntrada.buffer, empalme.fibraEntrada.fibra, { estado: 'libre' })
      }
      if (empalme.fibraSalida.fibraId) {
        marcarFibraEnEsteNodo(empalme.fibraSalida.cableId, empalme.fibraSalida.buffer, empalme.fibraSalida.fibra, { estado: 'libre' })
      }
    }
    const newBandejas = localConfig.bandejas.map(b => {
      if (b.nivel === bandejaNum) {
        return {
          ...b,
          empalmes: b.empalmes.filter(e => e.id !== empalmeId)
        }
      }
      return b
    })
    updateConfig({ bandejas: newBandejas })
  }

  /** Elige cable+fibra real para un lado (entrada/salida) de un empalme,
   *  liberando la fibra anterior de ese lado si cambió, y marcando la nueva. */
  const seleccionarFibraEmpalme = (
    bandejaNum: number,
    empalmeId: string,
    lado: 'fibraEntrada' | 'fibraSalida',
    cableId: string,
    fibraId: string
  ) => {
    const bandeja = localConfig.bandejas.find(b => b.nivel === bandejaNum)
    const empalme = bandeja?.empalmes.find(e => e.id === empalmeId)
    if (!empalme) return

    const anterior = empalme[lado]
    if (anterior.fibraId && (anterior.cableId !== cableId || anterior.fibraId !== fibraId)) {
      marcarFibraEnEsteNodo(anterior.cableId, anterior.buffer, anterior.fibra, { estado: 'libre' })
    }

    const cable = cablesDisponibles.find(c => c.id === cableId)
    const fibraInfo = cable ? listFibrasParaPicker(cable as any).find(f => f.fibraId === fibraId) : undefined
    if (!fibraInfo) return

    marcarFibraEnEsteNodo(cableId, fibraInfo.bufferNum, fibraInfo.fibraNum, { estado: 'fusionado' })

    updateEmpalme(bandejaNum, empalmeId, {
      [lado]: {
        cableId,
        buffer: fibraInfo.bufferNum,
        fibra: fibraInfo.fibraNum,
        color: fibraInfo.fibraColor,
        fibraId,
      },
    })
  }

  const updateEmpalme = (bandejaNum: number, empalmeId: string, updates: Partial<EmpalmeBandeja>) => {
    const newBandejas = localConfig.bandejas.map(b => {
      if (b.nivel === bandejaNum) {
        return {
          ...b,
          empalmes: b.empalmes.map(e => {
            if (e.id === empalmeId) {
              const updated = { ...e, ...updates }
              if (updates.tipo) {
                updated.atenuacion = ATENUACION_EMPALME[updates.tipo]
              }
              return updated
            }
            return e
          })
        }
      }
      return b
    })
    updateConfig({ bandejas: newBandejas })
  }

  const addSplitter = () => {
    const nuevoSplitter: SplitterCierre = {
      id: `SP-${Date.now()}`,
      tipo: 'balanceado',
      ratio: '1:4',
      bandeja: 1,
      posicion: localConfig.splitters.length + 1,
      salidasUsadas: 0,
      atenuacion: ATENUACION_SPLITTER['1:4']
    }
    updateConfig({ splitters: [...localConfig.splitters, nuevoSplitter] })
  }

  const removeSplitter = (id: string) => {
    updateConfig({ splitters: localConfig.splitters.filter(s => s.id !== id) })
  }

  const updateSplitter = (id: string, updates: Partial<SplitterCierre>) => {
    const newSplitters = localConfig.splitters.map(s => {
      if (s.id === id) {
        const updated = { ...s, ...updates }
        if (updates.ratio) {
          updated.atenuacion = ATENUACION_SPLITTER[updates.ratio]
        }
        return updated
      }
      return s
    })
    updateConfig({ splitters: newSplitters })
  }

  const addCableConectado = () => {
    const nuevoCable: CableConectado = {
      cableId: cablesDisponibles[0]?.id || `CABLE-${localConfig.cablesConectados.length + 1}`,
      direccion: 'entrada',
      fibrasUsadas: 0,
      puerto: localConfig.cablesConectados.length + 1
    }
    updateConfig({ cablesConectados: [...localConfig.cablesConectados, nuevoCable] })
  }

  const removeCableConectado = (index: number) => {
    updateConfig({ 
      cablesConectados: localConfig.cablesConectados.filter((_, i) => i !== index) 
    })
  }

  const updateCableConectado = (index: number, updates: Partial<CableConectado>) => {
    const newCables = [...localConfig.cablesConectados]
    newCables[index] = { ...newCables[index], ...updates }
    updateConfig({ cablesConectados: newCables })
  }

  const totalEmpalmes = localConfig.bandejas.reduce((acc, b) => acc + b.empalmes.length, 0)
  const capacidadUsada = totalEmpalmes + (localConfig.splitters.length * 2)

  const getValidationWarnings = () => {
    const warnings: string[] = []
    
    if (localConfig.splitters.some(s => !s.fibraEntrada)) {
      warnings.push('Hay splitters sin fibra de entrada asignada')
    }
    
    if (capacidadUsada > localConfig.capacidadFibras) {
      warnings.push(`Capacidad excedida: ${capacidadUsada}/${localConfig.capacidadFibras} fibras`)
    }
    
    const atenuacionTotal = localConfig.splitters.reduce((acc, s) => acc + s.atenuacion, 0) +
      localConfig.bandejas.reduce((acc, b) => 
        acc + b.empalmes.reduce((acc2, e) => acc2 + (e.atenuacion || 0), 0), 0)
    
    if (atenuacionTotal > 5) {
      warnings.push(`Atenuación total alta: ${atenuacionTotal.toFixed(1)} dB`)
    }
    
    return warnings
  }

  const warnings = getValidationWarnings()

  return (
    <div className="space-y-6">
      {/* Advertencias */}
      {warnings.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800 dark:text-yellow-200 text-sm">Advertencias</p>
              <ul className="mt-1 text-sm text-yellow-700 dark:text-yellow-300 list-disc list-inside">
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Información básica */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Tipo de Caja</label>
          <select
            value={localConfig.tipoCaja}
            onChange={(e) => updateConfig({ tipoCaja: e.target.value as TipoCaja })}
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          >
            {TIPOS_CAJA.map(tipo => (
              <option key={tipo} value={tipo}>{tipo}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Modelo</label>
          <input
            type="text"
            value={localConfig.modelo}
            onChange={(e) => updateConfig({ modelo: e.target.value })}
            placeholder="Ej: FOSC-400"
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Fabricante</label>
          <input
            type="text"
            value={localConfig.fabricante || ''}
            onChange={(e) => updateConfig({ fabricante: e.target.value })}
            placeholder="Ej: Corning"
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Capacidad (fibras)</label>
          <select
            value={localConfig.capacidadFibras}
            onChange={(e) => updateConfig({ capacidadFibras: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          >
            {CAPACIDADES_CIERRE.map(cap => (
              <option key={cap} value={cap}>{cap} fibras</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Número de Bandejas</label>
          <select
            value={localConfig.numeroBandejas}
            onChange={(e) => updateNumeroBandejas(parseInt(e.target.value))}
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          >
            {[1, 2, 3, 4, 5, 6, 8, 10, 12].map(n => (
              <option key={n} value={n}>{n} bandejas</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Estado</label>
          <select
            value={localConfig.estadoGeneral}
            onChange={(e) => updateConfig({ estadoGeneral: e.target.value as any })}
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          >
            <option value="operativo">🟢 Operativo</option>
            <option value="mantenimiento">🟡 En Mantenimiento</option>
            <option value="fuera_servicio">🔴 Fuera de Servicio</option>
          </select>
        </div>
      </div>

      {/* Cables Conectados */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium flex items-center gap-2">
            <Link2 className="w-4 h-4" />
            Cables Conectados
          </h4>
          <button
            type="button"
            onClick={addCableConectado}
            className="text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Agregar Cable
          </button>
        </div>

        {localConfig.cablesConectados.length === 0 ? (
          <div className="text-center py-4 text-gray-500 border-2 border-dashed rounded-lg">
            <Link2 className="w-6 h-6 mx-auto mb-1 opacity-50" />
            <p className="text-sm">Sin cables conectados</p>
          </div>
        ) : (
          <div className="space-y-2">
            {localConfig.cablesConectados.map((cable, index) => (
              <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-dark-700 rounded-lg">
                <select
                  value={cable.cableId}
                  onChange={(e) => updateCableConectado(index, { cableId: e.target.value })}
                  className="flex-1 px-2 py-1 text-sm border rounded dark:bg-dark-600 dark:border-dark-500"
                >
                  {cablesDisponibles.length > 0 ? (
                    cablesDisponibles.map(c => (
                      <option key={c.id} value={c.id}>{cableLabelForSelect(c as any)}</option>
                    ))
                  ) : (
                    <option value={cable.cableId}>{cable.cableId.slice(-8)}</option>
                  )}
                </select>
                <select
                  value={cable.direccion}
                  onChange={(e) => updateCableConectado(index, { direccion: e.target.value as any })}
                  className="w-24 px-2 py-1 text-sm border rounded dark:bg-dark-600 dark:border-dark-500"
                >
                  <option value="entrada">Entrada</option>
                  <option value="salida">Salida</option>
                  <option value="paso">Paso</option>
                </select>
                <span className="text-xs text-gray-500">P{cable.puerto || index + 1}</span>
                <button
                  type="button"
                  onClick={() => removeCableConectado(index)}
                  className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bandejas y Empalmes */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Bandejas de Empalme
          </h4>
          <div className="text-sm text-gray-500">
            {totalEmpalmes} empalmes configurados
          </div>
        </div>

        <div className="space-y-2">
          {localConfig.bandejas.map((bandeja) => (
            <div key={bandeja.nivel} className="border rounded-lg dark:border-dark-600 overflow-hidden">
              <button
                type="button"
                onClick={() => setExpandedBandeja(expandedBandeja === bandeja.nivel ? null : bandeja.nivel)}
                className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-dark-700"
              >
                <div className="flex items-center gap-2">
                  <Box className="w-4 h-4 text-blue-500" />
                  <span className="font-medium">Bandeja {bandeja.nivel}</span>
                  <span className="text-sm text-gray-500">
                    ({bandeja.empalmes.length}/{bandeja.capacidad} empalmes)
                  </span>
                </div>
                <span>{expandedBandeja === bandeja.nivel ? '▼' : '▶'}</span>
              </button>

              {expandedBandeja === bandeja.nivel && (
                <div className="p-3 bg-gray-50 dark:bg-dark-800 border-t dark:border-dark-600">
                  <div className="flex justify-end mb-2">
                    <button
                      type="button"
                      onClick={() => addEmpalme(bandeja.nivel)}
                      disabled={bandeja.empalmes.length >= bandeja.capacidad}
                      className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      + Empalme
                    </button>
                  </div>

                  {bandeja.empalmes.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-2">Sin empalmes en esta bandeja</p>
                  ) : (
                    <div className="space-y-2">
                      {bandeja.empalmes.map((empalme) => (
                        <div key={empalme.id} className="p-2 bg-white dark:bg-dark-700 rounded border dark:border-dark-600">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium">Pos {empalme.posicion}</span>
                            <div className="flex items-center gap-2">
                              <select
                                value={empalme.tipo}
                                onChange={(e) => updateEmpalme(bandeja.nivel, empalme.id, { tipo: e.target.value as TipoEmpalme })}
                                className="text-xs px-1 py-0.5 border rounded dark:bg-dark-600"
                              >
                                <option value="fusión">Fusión</option>
                                <option value="mecánico">Mecánico</option>
                              </select>
                              <span className="text-xs text-orange-600">{empalme.atenuacion} dB</span>
                              <button
                                type="button"
                                onClick={() => removeEmpalme(bandeja.nivel, empalme.id)}
                                className="p-0.5 text-red-500 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {(['fibraEntrada', 'fibraSalida'] as const).map((lado) => {
                              const valor = empalme[lado]
                              const cableSeleccionado = cablesDisponibles.find(c => c.id === valor.cableId)
                              const fibrasDisponibles = cableSeleccionado ? listFibrasParaPicker(cableSeleccionado as any) : []
                              return (
                                <div key={lado}>
                                  <label className="text-gray-500">{lado === 'fibraEntrada' ? 'Entrada:' : 'Salida:'}</label>
                                  <div className="flex flex-col gap-1 mt-1">
                                    <select
                                      value={valor.cableId}
                                      onChange={(e) => {
                                        const cableId = e.target.value
                                        if (valor.fibraId) {
                                          marcarFibraEnEsteNodo(valor.cableId, valor.buffer, valor.fibra, { estado: 'libre' })
                                        }
                                        updateEmpalme(bandeja.nivel, empalme.id, {
                                          [lado]: { cableId, fibra: 0, color: COLORES_FIBRA[0] },
                                        } as Partial<EmpalmeBandeja>)
                                      }}
                                      className="px-1 py-0.5 border rounded dark:bg-dark-600 text-xs"
                                    >
                                      <option value="">Seleccionar cable...</option>
                                      {cablesDisponibles.map(c => (
                                        <option key={c.id} value={c.id}>{cableLabelForSelect(c as any)}</option>
                                      ))}
                                    </select>
                                    <select
                                      value={valor.fibraId || ''}
                                      onChange={(e) => {
                                        if (e.target.value) {
                                          seleccionarFibraEmpalme(bandeja.nivel, empalme.id, lado, valor.cableId, e.target.value)
                                        }
                                      }}
                                      disabled={!valor.cableId}
                                      className="px-1 py-0.5 border rounded dark:bg-dark-600 text-xs disabled:opacity-50"
                                    >
                                      <option value="">Seleccionar fibra...</option>
                                      {fibrasDisponibles.map(f => (
                                        <option key={f.fibraId} value={f.fibraId} disabled={f.estado !== 'libre' && f.fibraId !== valor.fibraId}>
                                          {f.bufferNum ? `B${f.bufferNum}-` : ''}H{f.fibraNum} ({f.fibraColor}){f.estado !== 'libre' ? ` [${f.estado}]` : ''}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Splitters de Primer Nivel */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium flex items-center gap-2">
            <GitBranch className="w-4 h-4" />
            Splitters de 1er Nivel
          </h4>
          <button
            type="button"
            onClick={addSplitter}
            className="text-sm px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Agregar Splitter
          </button>
        </div>

        {localConfig.splitters.length === 0 ? (
          <div className="text-center py-4 text-gray-500 border-2 border-dashed rounded-lg">
            <GitBranch className="w-6 h-6 mx-auto mb-1 opacity-50" />
            <p className="text-sm">Sin splitters (empalmes directos)</p>
            <p className="text-xs">Agregue splitters para derivación de señal</p>
          </div>
        ) : (
          <div className="space-y-2">
            {localConfig.splitters.map((splitter) => (
              <div key={splitter.id} className="p-3 bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                    Splitter {splitter.ratio}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeSplitter(splitter.id)}
                    className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">Ratio</label>
                    <select
                      value={splitter.ratio}
                      onChange={(e) => updateSplitter(splitter.id, { ratio: e.target.value as RatioSplitter })}
                      className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                    >
                      {RATIOS_SPLITTER.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Bandeja</label>
                    <select
                      value={splitter.bandeja}
                      onChange={(e) => updateSplitter(splitter.id, { bandeja: parseInt(e.target.value) })}
                      className="w-full mt-1 px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
                    >
                      {localConfig.bandejas.map(b => (
                        <option key={b.nivel} value={b.nivel}>Bandeja {b.nivel}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Atenuación</label>
                    <div className="mt-1 px-2 py-1 text-sm bg-orange-100 dark:bg-orange-900/30 rounded text-center text-orange-700 dark:text-orange-300">
                      {splitter.atenuacion} dB
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Resumen */}
      <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
        <h4 className="font-medium mb-2">Resumen del Cierre</h4>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Capacidad:</span>
            <span className="ml-2 font-medium">{capacidadUsada}/{localConfig.capacidadFibras}</span>
          </div>
          <div>
            <span className="text-gray-500">Empalmes:</span>
            <span className="ml-2 font-medium">{totalEmpalmes}</span>
          </div>
          <div>
            <span className="text-gray-500">Splitters:</span>
            <span className="ml-2 font-medium">{localConfig.splitters.length}</span>
          </div>
        </div>
        {localConfig.splitters.length > 0 && (
          <div className="mt-2 pt-2 border-t text-sm text-orange-600">
            Atenuación total splitters: {localConfig.splitters.reduce((acc, s) => acc + s.atenuacion, 0).toFixed(1)} dB
          </div>
        )}
      </div>
    </div>
  )
}
